import React, { useEffect, useState } from "react"
import {
  Table,
  Typography,
  Select,
  Row,
  Col,
  Tooltip,
  DatePicker,
  Button,
  Popover,
  Checkbox,
  Input,
} from "antd"
import { InfoCircleOutlined, SearchOutlined } from "@ant-design/icons"
import { collection, onSnapshot, query, where } from "firebase/firestore"
import { db } from "../../../firebase/config"
import { OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"
import { endDate } from "../../../components/Orders/Helpers/TableDetails"
import * as XLSX from "xlsx"

const { Text } = Typography
const { Option } = Select
const { RangePicker } = DatePicker

const COLUMN_KEYS = [
  { key: "Nr.", label: "Nr." },
  { key: "Client", label: "Client" },
  { key: "Product", label: "Product" },
  { key: "Manager", label: "Manager" },
  { key: "Machine", label: "Machine" },
  { key: "Quantity", label: "Quantity" },
  { key: "Pricing Type", label: "Pricing Type" },
  { key: "System Price", label: "System Price" },
  { key: "Price", label: "Price" },
  { key: "Difference", label: "Difference" },
  { key: "Order System Price", label: "Order System Price" },
  { key: "Order Price", label: "Order Price" },
  { key: "Order Difference", label: "Order Difference" },
  { key: "Ended", label: "Ended" },
]

export default function SystemOrderCosts() {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedManager, setSelectedManager] = useState(null)
  const [dateRange, setDateRange] = useState([null, null])
  const [hiddenColumns, setHiddenColumns] = useState([])
  const [selectedPrefixes, setSelectedPrefixes] = useState([])
  const [orderSearch, setOrderSearch] = useState("") // ✅ NEW

  useEffect(() => {
    setLoading(true)
    const ref = query(
      collection(db, "orders"),
      where("hasSystemPrice", "==", true),
      where("orderStatus", "==", 7),
    )

    const unsubscribe = onSnapshot(ref, (snapshot) => {
      const results = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }))
      setData(results)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const managers = [...new Set(data.map((item) => item.user).filter(Boolean))]

  const filteredData = data.filter((item) => {
    if (selectedManager && item.user !== selectedManager) return false
    if (dateRange[0] && dateRange[1]) {
      const endedSeconds = item.doneByRewinder?.seconds
      if (!endedSeconds) return false
      const endedMs = endedSeconds * 1000
      const from = dateRange[0].valueOf()
      const to = dateRange[1].clone().endOf("day").valueOf()
      if (endedMs < from || endedMs > to) return false
    }
    // ✅ NEW: match against justOrderNumber (e.g. "2704") or full orderNumber (e.g. "#ON2704")
    if (orderSearch.trim()) {
      const term = orderSearch.trim().toLowerCase()
      const matchesNumber = String(item.justOrderNumber).includes(term)
      const matchesFull = item.orderNumber?.toLowerCase().includes(term)
      if (!matchesNumber && !matchesFull) return false
    }
    return true
  })

  const exportData =
    selectedPrefixes.length > 0
      ? filteredData.filter((r) => selectedPrefixes.includes(r.manufacturer?.invoicePrefix))
      : filteredData

  const clientFilters = data.reduce((acc, item) => {
    const name = item.client?.client
    if (name && !acc.find((f) => f.text === name)) acc.push({ text: name, value: name })
    return acc
  }, [])

  const productFilters = data.reduce((acc, item) => {
    const name = item.selectedProduct?.product?.productName
    if (name && !acc.find((f) => f.text === name)) acc.push({ text: name, value: name })
    return acc
  }, [])

  const prefixFilters = data.reduce((acc, item) => {
    const prefix = item.manufacturer?.invoicePrefix
    if (prefix && !acc.find((f) => f.text === prefix)) acc.push({ text: prefix, value: prefix })
    return acc
  }, [])

  const toggleColumn = (key) => {
    setHiddenColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    )
  }

  const exportToXlsx = () => {
    const rows = exportData.map((r) => {
      const orderSystemPrice =
        r.labelCount != null && r.systemPriceForOrder != null
          ? (r.labelCount / 1000) * r.systemPriceForOrder
          : null
      const orderPrice =
        r.labelCount != null && r.price != null ? (r.labelCount / 1000) * r.price : null
      const orderDiff =
        orderSystemPrice != null && orderPrice != null ? orderPrice - orderSystemPrice : null
      const priceDiff =
        r.systemPriceForOrder != null && r.price != null ? r.price - r.systemPriceForOrder : null

      const endedDate = r.doneByRewinder?.seconds
        ? new Date(r.doneByRewinder.seconds * 1000).toLocaleDateString("lv-LV")
        : ""

      return {
        "Nr.": r.orderNumber,
        Client: r.client?.client ?? "",
        Product: r.selectedProduct?.product?.productName ?? "",
        "Knife Code": r.selectedProduct?.knifeCode ?? "",
        Manager: r.user ?? "",
        Machine: r.selectedProduct?.machine ?? "",
        "Original Machine": r.originalMachine ?? "",
        Quantity: r.labelCount ?? "",
        "Pricing Type": r.selectedProduct?.advanced?.fixedPrice ? "Fixed" : "Dynamic",
        "System Price (per 1000)": r.systemPriceForOrder ?? "",
        "Price (per 1000)": r.price ?? "",
        "Difference (per 1000)": priceDiff != null ? parseFloat(priceDiff.toFixed(2)) : "",
        "Order System Price":
          orderSystemPrice != null ? parseFloat(orderSystemPrice.toFixed(2)) : "",
        "Order Price": orderPrice != null ? parseFloat(orderPrice.toFixed(2)) : "",
        "Order Difference": orderDiff != null ? parseFloat(orderDiff.toFixed(2)) : "",
        Ended: endedDate,
      }
    })

    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Orders")
    XLSX.writeFile(wb, `orders_export_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  const columns = [
    {
      key: "Nr.",
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "center",
      width: 200,
      sorter: (a, b) => a.justOrderNumber - b.justOrderNumber,
      render: (_, record) => OrderAndPrefix(record),
    },
    {
      key: "Client",
      title: "Client",
      dataIndex: ["client", "client"],
      align: "center",
      width: 130,
      filters: clientFilters,
      onFilter: (value, record) => record.client?.client === value,
      filterSearch: true,
      ellipsis: true,
      render: (value) => <Text copyable={{ text: value }}>{value}</Text>,
    },
    {
      key: "Product",
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "center",
      width: 260,
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct?.product?.productName === value,
      filterSearch: true,
      ellipsis: true,
      render: (value) => <Text copyable={{ text: value }}>{value}</Text>,
    },
    {
      key: "Manager",
      title: "Manager",
      dataIndex: "user",
      align: "center",
      ellipsis: true,
      width: 120,
      render: (value) => <Text type="secondary">{value}</Text>,
    },
    {
      key: "Prefix",
      title: "Prefix",
      dataIndex: ["manufacturer", "invoicePrefix"],
      align: "center",
      ellipsis: true,
      width: 80,
      filters: prefixFilters,
      filteredValue: selectedPrefixes.length ? selectedPrefixes : null,
      onFilter: (value, record) => record.manufacturer?.invoicePrefix === value,
    },
    {
      key: "Machine",
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      align: "center",
      width: 200,
      filters: [
        { text: "Viva", value: "Viva" },
        { text: "Arsoma", value: "Arsoma" },
        { text: "Tape", value: "Tape" },
        { text: "Cutter", value: "Cutter" },
      ],
      onFilter: (value, record) => record.selectedProduct?.machine === value,
      render: (value, record) => {
        const original = record.originalMachine
        const changed = original && original !== value
        return (
          <span>
            {value}
            {changed && (
              <Tooltip title={`Originally: ${original}`}>
                <span style={{ color: "orange", marginLeft: 4, cursor: "default" }}>
                  ⚠ {original} → {value}
                </span>
              </Tooltip>
            )}
          </span>
        )
      },
    },
    {
      key: "Quantity",
      title: "Quantity",
      dataIndex: "labelCount",
      align: "center",
      width: 120,
      sorter: (a, b) => a.labelCount - b.labelCount,
      render: (value) => <Text>{value?.toLocaleString()}</Text>,
    },
    {
      key: "Pricing Type",
      title: "Pricing Type",
      align: "center",
      width: 130,
      filters: [
        { text: "Fixed", value: true },
        { text: "Dynamic", value: false },
      ],
      onFilter: (value, record) => record.selectedProduct?.advanced?.fixedPrice === value,
      render: (_, rec) => {
        const isFixed = rec.selectedProduct?.advanced?.fixedPrice
        return (
          <Text style={{ color: isFixed ? "red" : "blue" }}>{isFixed ? "Fixed" : "Dynamic"}</Text>
        )
      },
    },
    {
      key: "System Price",
      title: (
        <Tooltip title="Price per 1000 labels calculated by the system">
          System Price <InfoCircleOutlined style={{ marginLeft: 4 }} />
        </Tooltip>
      ),
      dataIndex: "systemPriceForOrder",
      align: "center",
      width: 160,
      sorter: (a, b) => a.systemPriceForOrder - b.systemPriceForOrder,
      render: (value) =>
        value != null ? <Text>{value.toFixed(2)} €</Text> : <Text type="secondary">—</Text>,
    },
    {
      key: "Price",
      title: <Tooltip title="Sale price per 1000 labels">Price</Tooltip>,
      dataIndex: "price",
      align: "center",
      width: 160,
      sorter: (a, b) => a.price - b.price,
      render: (value) =>
        value != null ? <Text>{value.toFixed(2)} €</Text> : <Text type="secondary">—</Text>,
    },
    {
      key: "Difference",
      title: (
        <Tooltip title="Difference between system price and sale price">
          Difference <InfoCircleOutlined style={{ marginLeft: 4 }} />
        </Tooltip>
      ),
      align: "center",
      width: 120,
      render: (_, rec) => {
        const system = rec.systemPriceForOrder
        const sale = rec.price
        if (system == null || sale == null) return <Text type="secondary">—</Text>
        const diff = sale - system
        const color = Math.abs(diff) < 0.01 ? "blue" : diff > 0 ? "green" : "red"
        return (
          <Text style={{ color }}>
            {diff > 0 ? "+" : ""}
            {diff.toFixed(2)} €
          </Text>
        )
      },
    },
    {
      key: "Order System Price",
      title: "Order System Price",
      align: "center",
      width: 200,
      sorter: (a, b) =>
        (a.labelCount / 1000) * a.systemPriceForOrder -
        (b.labelCount / 1000) * b.systemPriceForOrder,
      render: (_, rec) => {
        if (rec.systemPriceForOrder == null || rec.labelCount == null)
          return <Text type="secondary">—</Text>
        const total = (rec.labelCount / 1000) * rec.systemPriceForOrder
        return <Text>{total.toFixed(2)} €</Text>
      },
    },
    {
      key: "Order Price",
      title: "Order Price",
      align: "center",
      width: 200,
      sorter: (a, b) => (a.labelCount / 1000) * a.price - (b.labelCount / 1000) * b.price,
      render: (_, rec) => {
        if (rec.price == null || rec.labelCount == null) return <Text type="secondary">—</Text>
        const total = (rec.labelCount / 1000) * rec.price
        return <Text>{total.toFixed(2)} €</Text>
      },
    },
    {
      key: "Order Difference",
      title: (
        <Tooltip title="Difference between total sale price and total system price">
          Order Difference <InfoCircleOutlined style={{ marginLeft: 4 }} />
        </Tooltip>
      ),
      align: "center",
      width: 200,
      render: (_, rec) => {
        if (rec.systemPriceForOrder == null || rec.price == null || rec.labelCount == null)
          return <Text type="secondary">—</Text>
        const totalSystem = (rec.labelCount / 1000) * rec.systemPriceForOrder
        const totalSale = (rec.labelCount / 1000) * rec.price
        const diff = totalSale - totalSystem
        const color = Math.abs(diff) < 0.01 ? "green" : diff > 0 ? "blue" : "red"
        return (
          <Text style={{ color }}>
            {diff > 0 ? "+" : ""}
            {diff.toFixed(2)} €
          </Text>
        )
      },
    },
    {
      key: "Ended",
      title: "Ended",
      align: "center",
      dataIndex: "doneByRewinder",
      width: 100,
      defaultSortOrder: "descend",
      sorter: (a, b) => a.doneByRewinder?.seconds - b.doneByRewinder?.seconds,
      render: (value, record) => endDate(value, record),
    },
  ]

  const visibleColumns = columns.filter((col) => !hiddenColumns.includes(col.key))

  const summary = (pageData) => {
    const totalSystemEur = pageData.reduce(
      (sum, r) => sum + (r.labelCount / 1000) * (r.systemPriceForOrder ?? 0),
      0,
    )
    const totalSaleEur = pageData.reduce(
      (sum, r) => sum + (r.labelCount / 1000) * (r.price ?? 0),
      0,
    )
    const totalDiff = totalSaleEur - totalSystemEur

    return (
      <Table.Summary fixed="bottom">
        <Table.Summary.Row style={{ background: "#fafafa", fontWeight: 600 }}>
          {visibleColumns.map((col, index) => {
            if (index === 0) {
              return (
                <Table.Summary.Cell key={col.key} index={index} align="right">
                  <Text strong>Total ({pageData.length})</Text>
                </Table.Summary.Cell>
              )
            }
            if (col.key === "Order System Price") {
              return (
                <Table.Summary.Cell key={col.key} index={index} align="center">
                  <Text strong>{totalSystemEur.toFixed(2)} €</Text>
                </Table.Summary.Cell>
              )
            }
            if (col.key === "Order Price") {
              return (
                <Table.Summary.Cell key={col.key} index={index} align="center">
                  <Text strong>{totalSaleEur.toFixed(2)} €</Text>
                </Table.Summary.Cell>
              )
            }
            if (col.key === "Order Difference") {
              return (
                <Table.Summary.Cell key={col.key} index={index} align="center">
                  <Text strong style={{ color: totalDiff >= 0 ? "blue" : "red" }}>
                    {totalDiff > 0 ? "+" : ""}
                    {totalDiff.toFixed(2)} €
                  </Text>
                </Table.Summary.Cell>
              )
            }
            return <Table.Summary.Cell key={col.key} index={index} />
          })}
        </Table.Summary.Row>
      </Table.Summary>
    )
  }

  const columnPopover = (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 24px" }}>
      {COLUMN_KEYS.map(({ key, label }) => (
        <Checkbox
          key={key}
          checked={!hiddenColumns.includes(key)}
          onChange={() => toggleColumn(key)}>
          {label}
        </Checkbox>
      ))}
    </div>
  )

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }} align="middle">
        <Col>
          <Text strong>Manager:</Text>
        </Col>
        <Col>
          <Select
            allowClear
            placeholder="All managers"
            style={{ width: 240 }}
            value={selectedManager}
            onChange={(val) => setSelectedManager(val ?? null)}>
            {managers.map((m) => (
              <Option key={m} value={m}>
                {m}
              </Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Text strong>Completed date:</Text>
        </Col>
        <Col>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates ?? [null, null])}
            format="DD.MM.YYYY"
            allowClear
          />
        </Col>
        {/* ✅ NEW: order number search */}
        <Col>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search order nr."
            style={{ width: 180 }}
            value={orderSearch}
            onChange={(e) => setOrderSearch(e.target.value)}
          />
        </Col>
        {(selectedManager || dateRange[0]) && (
          <Col>
            <Text type="secondary">
              Showing {filteredData.length} of {data.length} orders
            </Text>
          </Col>
        )}
        <Col style={{ marginLeft: "auto" }}>
          <Row gutter={8}>
            <Col>
              <Popover
                content={columnPopover}
                title="Show / Hide Columns"
                trigger="click"
                placement="bottomRight">
                <Button>
                  Columns {hiddenColumns.length > 0 && `(${hiddenColumns.length} hidden)`}
                </Button>
              </Popover>
            </Col>
            <Col>
              <Button type="primary" onClick={exportToXlsx}>
                Export to XLSX ({exportData.length})
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>

      <Table
        virtual
        size="small"
        loading={loading}
        columns={visibleColumns}
        bordered
        dataSource={filteredData}
        rowKey={(record) => record.id}
        pagination={false}
        summary={summary}
        scroll={{ x: "max-content", y: "calc(100vh - 120px)" }}
        rowHeight={39}
        onChange={(_, filters) => setSelectedPrefixes(filters["Prefix"] ?? [])}
      />
    </div>
  )
}
